// walkthrough/resolve.js — K843: the honesty primitive.
//
// THE RULE THIS FILE EXISTS TO ENFORCE:
//   the assistant may not say "click X" until a resolve for X has SUCCEEDED.
//
// Everything else in the walkthrough is presentation. This is the part that
// stops the failure that would discredit the feature: pointing at a control
// that is not there, is not this record's, or the user's role cannot use.
//
// Driver.js (and every tour library) silently takes the FIRST match for a
// selector. A walkthrough that points at the wrong one of three Approve
// buttons is worse than one that fails loudly, so ambiguity is a stop, not a
// tie-break. The generated manifest already flags multi-site anchors and CI
// refuses a guide that names one without binding a resource; this is the
// runtime half of the same guarantee.

/* Resolution order. One rule, no contradiction:
     1 curated  data-g360-anchor  -> RESOLUTION. A deliberate promise.
     2 testid   data-testid       -> CONTROLLED FALLBACK, allow-listed per
                                     guide. A test id is not a promise; it
                                     belongs to whoever owns the test.
     3 role + accessible name     -> VERIFICATION ONLY, NEVER RESOLUTION.
                                     The accessible name is the TRANSLATED
                                     string. A translated "Approve" must
                                     never become an unreviewed fallback for
                                     a consequential control. */
const TIER = { CURATED: 'curated', TESTID: 'testid' };

/* Exported for page-anchors.js, which needs the same escaping to build an
   attribute selector. It was duplicated there first - the same helper, with
   the same fallback, written again because nobody looked. One definition. */
export function cssEscape(v) {
  return (window.CSS && CSS.escape) ? CSS.escape(v) : String(v).replace(/["\\]/g, '\\$&');
}

/* The container a match must live inside. A resource binding is what makes
   "the save button" mean "THIS rule's save button" rather than the first one
   on the page. */
function scopeRoot(binding) {
  const { panel, resource_id } = binding || {};
  if (resource_id) {
    const byRes = document.querySelector(
      `[data-g360-resource="${cssEscape(resource_id)}"]`);
    if (byRes) return byRes;
  }
  if (panel) {
    const byPanel = document.querySelector(
      `[data-g360-panel="${cssEscape(panel)}"]`);
    if (byPanel) return byPanel;
  }
  return document;
}

function matchesIn(root, anchorId, allowTestid) {
  const curated = [...root.querySelectorAll(
    `[data-g360-anchor="${cssEscape(anchorId)}"]`)];
  if (curated.length) return { els: curated, matched_by: TIER.CURATED };
  if (!allowTestid) return { els: [], matched_by: null };
  const tid = [...root.querySelectorAll(
    `[data-testid="${cssEscape(anchorId)}"]`)];
  return { els: tid, matched_by: tid.length ? TIER.TESTID : null };
}

/* Visible means a user can actually see it, not merely that it is in the DOM.
   offsetParent is null for display:none and for fixed elements, so the rect
   is checked too. An element inside a collapsed accordion or an inactive tab
   reads as not visible, which is correct: the guide must open it first
   rather than pointing into a closed drawer. */
function isVisible(el) {
  if (!el || !el.isConnected) return false;
  const cs = getComputedStyle(el);
  if (cs.display === 'none' || cs.visibility === 'hidden' || cs.visibility === 'collapse') return false;
  if (Number(cs.opacity) === 0) return false;
  const r = el.getBoundingClientRect();
  return (r.width > 0 && r.height > 0);
}

/* Enabled, and WHY NOT when it is not. "requires Owner role" is the
   difference between the assistant saying something useful and saying
   nothing. The reason is read from the DOM, never inferred by the model. */
function enabledState(el) {
  if (el.disabled === true) {
    return { enabled: false, reason: el.getAttribute('data-g360-disabled-reason') || 'the control is disabled' };
  }
  if (el.getAttribute('aria-disabled') === 'true') {
    return { enabled: false, reason: el.getAttribute('data-g360-disabled-reason') || 'the control is marked unavailable' };
  }
  const fs = el.closest('fieldset[disabled]');
  if (fs) return { enabled: false, reason: 'the whole section is disabled' };
  return { enabled: true, reason: null };
}

/* Verification tier. Does NOT resolve; it only reports whether the element we
   already found looks like what the guide meant. A mismatch is surfaced, not
   acted on - the accessible name is translated, so a mismatch may simply mean
   the console is in another language. */
function verify(el, binding) {
  const want = binding && binding.expect;
  if (!want) return { checked: false, ok: null, note: null };
  const role = el.getAttribute('role') || el.tagName.toLowerCase();
  const name = (el.getAttribute('aria-label')
             || el.textContent || '').trim().replace(/\s+/g, ' ').slice(0, 120);
  const roleOk = !want.role || want.role === role;
  const nameOk = !want.name_contains
    || name.toLowerCase().includes(String(want.name_contains).toLowerCase());
  return {
    checked: true,
    ok: roleOk && nameOk,
    note: (roleOk && nameOk) ? null
      : `expected role '${want.role || 'any'}' and a name containing '${want.name_contains || ''}', saw role '${role}' and '${name}'. The accessible name is translated, so this may be a language difference rather than the wrong control.`,
  };
}

export function appBuild() {
  const s = document.querySelector('script[src*="/assets/index-"]');
  const m = s && s.getAttribute('src').match(/index-([A-Za-z0-9_-]+)\.js/);
  return m ? m[1] : null;
}

/* The contract. Every field the engine needs to decide whether the assistant
   is allowed to speak, and nothing the model can override. */
export function resolveNow(binding, opts = {}) {
  const { anchor_id, resource_id = null, route_id = null, panel = null } = binding || {};
  const base = {
    anchor_id: anchor_id || null, resource_id, route_id, panel,
    found: false, visible: false, enabled: false, ambiguous: false,
    disabled_reason: null, matched_by: null, match_count: 0,
    verification: { checked: false, ok: null, note: null },
    app_build: appBuild(),
    route_matches: route_id ? (location.hash.replace(/^#/, '') === route_id) : null,
  };
  if (!anchor_id) return { ...base, error: 'no anchor_id in the binding' };

  const root = scopeRoot(binding);
  const { els, matched_by } = matchesIn(root, anchor_id, opts.allowTestid !== false);
  if (!els.length) return base;

  if (els.length > 1) {
    /* STOP. Not "take the first". The engine reports which sites matched so a
       guide author can add a resource binding, and the assistant says it
       cannot tell which one rather than guessing. */
    return { ...base, found: true, ambiguous: true, match_count: els.length, matched_by };
  }

  const el = els[0];
  const vis = isVisible(el);
  const en = enabledState(el);
  return {
    ...base,
    found: true, visible: vis, enabled: vis && en.enabled,
    disabled_reason: en.enabled ? null : en.reason,
    matched_by, match_count: 1,
    verification: verify(el, binding),
    element: el,
  };
}

/* Wait for a control that has not rendered yet.
   The console ships 198 lazy chunks, so a cold route chunk on a slow
   connection can take well past the 1000 ms some libraries default to - and
   one of them ADVANCES PAST THE STEP on timeout. Default here is 10 s, and a
   timeout returns found:false rather than advancing.
   MutationObserver, not polling: lower latency and fewer wakeups. */
export function resolveWhenReady(binding, opts = {}) {
  const timeoutMs = opts.timeoutMs == null ? 10000 : opts.timeoutMs;
  const first = resolveNow(binding, opts);
  if (first.found) return Promise.resolve(first);

  return new Promise((resolve) => {
    let done = false;
    const finish = (r) => {
      if (done) return;
      done = true;
      obs.disconnect(); clearTimeout(timer);
      resolve(r);
    };
    const obs = new MutationObserver(() => {
      const r = resolveNow(binding, opts);
      if (r.found) finish(r);
    });
    obs.observe(document.body, { childList: true, subtree: true });
    const timer = setTimeout(() => finish({ ...resolveNow(binding, opts), timed_out: true }), timeoutMs);
  });
}

/* The gate. The engine calls this; the assistant never decides for itself.
   Returns the sentence to say when guidance is NOT permitted, so the refusal
   is as specific as the instruction would have been - which is the whole
   argument for doing this with an assistant rather than a scripted tour. */
export function guidancePermitted(res) {
  if (!res || !res.found) {
    return { ok: false, say: `I can't find that control on screen${res && res.route_matches === false ? ' — you may be on a different page than I expected' : ''}. Tell me what you can see and I'll re-check.` };
  }
  if (res.ambiguous) {
    return { ok: false, say: `I can see ${res.match_count} controls that match, so I can't tell you which one to use without knowing the record. Which one are you working on?` };
  }
  if (!res.visible) {
    return { ok: false, say: `That control exists but isn't visible yet — it's probably inside a collapsed section or an inactive tab. I'll open it first.` };
  }
  if (!res.enabled) {
    return { ok: false, say: `That control is there but not available to you: ${res.disabled_reason}.` };
  }
  return { ok: true, say: null };
}
